"use client";

import { c as _c } from "react/compiler-runtime";
import { MapPin, Search, X } from "lucide-react";
import type React from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { OutOfAreaNotice } from "~/app/(site)/_components/OutOfAreaNotice";
import { DateTimeFields } from "~/app/(site)/search/_components/DateTimeFields";
import { VehicleSizePicker } from "~/app/(site)/search/_components/VehicleSizePicker";
import { Button } from "~/components/ui/button";
import { refreshNowSlot, useSearchDraft } from "~/hooks/useSearchWindow";
import { safeUUID } from "~/lib/auth/uuid";
import { type PlaceSuggestion, retrievePlace, suggestPlaces } from "~/lib/mapbox/mapbox";
import { cn } from "~/lib/utils";
import { formatShort } from "~/lib/utils/datetime";
interface MobileSearchSheetProps {
  open: boolean;
  onClose: () => void;
}

/** A Radix layer portals outside the sheet and handles its own keys. */
function isInPortaledLayer(target: EventTarget | null): boolean {
  return !!(target as Element | null)?.closest?.("[data-radix-popper-content-wrapper],[data-slot='select-content']");
}
export function MobileSearchSheet(t0) {
  const $ = _c(127);
  const {
    open,
    onClose
  } = t0;
  const locationInputRef = useRef(null);
  const locationRef = useRef(null);
  const sheetRef = useRef(null);
  const {
    q: location,
    place: selectedPlace,
    starts_at: startsAt,
    ends_at: endsAt,
    vehicleSize,
    now,
    timeZone: destTimeZone,
    searchUrl,
    setLocation,
    setPlace,
    setStartsAt,
    setEndsAt,
    setVehicleSize,
    reseedFromUrl,
    commitSearch,
    hasPendingChanges,
    outOfArea
  } = useSearchDraft();
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [suggestions, setSuggestions] = useState(t1);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [activeSuggestionIndex, setActiveSuggestionIndex] = useState(-1);
  const [isLoading, setIsLoading] = useState(false);
  const [retrieveFailed, setRetrieveFailed] = useState(false);
  const [sessionToken] = useState(_temp);
  const debounceRef = useRef(null);
  const suggestRequestRef = useRef(0);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      suggestRequestRef.current = suggestRequestRef.current + 1;
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
      setSuggestions([]);
      setShowSuggestions(false);
      setActiveSuggestionIndex(-1);
      setIsLoading(false);
      setRetrieveFailed(false);
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const clearSuggestions = t2;
  let t3;
  let t4;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      const handler = e => {
        if (!locationRef.current?.contains(e.target as Node)) {
          clearSuggestions();
        }
      };
      document.addEventListener("mousedown", handler);
      return () => document.removeEventListener("mousedown", handler);
    };
    t4 = [clearSuggestions];
    $[2] = t3;
    $[3] = t4;
  } else {
    t3 = $[2];
    t4 = $[3];
  }
  useEffect(t3, t4);
  const reseedRef = useRef(reseedFromUrl);
  let t5;
  let t6;
  if ($[4] !== reseedFromUrl) {
    t5 = () => {
      reseedRef.current = reseedFromUrl;
    };
    t6 = [reseedFromUrl];
    $[4] = reseedFromUrl;
    $[5] = t5;
    $[6] = t6;
  } else {
    t5 = $[5];
    t6 = $[6];
  }
  useEffect(t5, t6);
  let t7;
  let t8;
  if ($[7] !== open) {
    t7 = () => {
      if (open) {
        refreshNowSlot();
        reseedRef.current();
        clearSuggestions();
        setTimeout(() => locationInputRef.current?.focus(), 150);
      }
    };
    t8 = [open, clearSuggestions];
    $[7] = open;
    $[8] = t7;
    $[9] = t8;
  } else {
    t7 = $[8];
    t8 = $[9];
  }
  useEffect(t7, t8);
  let t10;
  let t9;
  if ($[10] !== onClose || $[11] !== open || $[12] !== showSuggestions) {
    t9 = () => {
      if (!open) {
        return;
      }
      const handler_0 = e_0 => {
        if (e_0.key !== "Escape") {
          return;
        }
        if (isInPortaledLayer(e_0.target)) {
          return;
        }
        if (showSuggestions) {
          clearSuggestions();
          return;
        }
        onClose();
      };
      document.addEventListener("keydown", handler_0);
      return () => document.removeEventListener("keydown", handler_0);
    };
    t10 = [open, showSuggestions, onClose, clearSuggestions];
    $[10] = onClose;
    $[11] = open;
    $[12] = showSuggestions;
    $[13] = t10;
    $[14] = t9;
  } else {
    t10 = $[13];
    t9 = $[14];
  }
  useEffect(t9, t10);
  let t11;
  let t12;
  if ($[15] !== open) {
    t11 = () => {
      if (!open) {
        return;
      }
      document.body.style.overflow = "hidden";
      return _temp2;
    };
    t12 = [open];
    $[15] = open;
    $[16] = t11;
    $[17] = t12;
  } else {
    t11 = $[16];
    t12 = $[17];
  }
  useEffect(t11, t12);
  let t13;
  let t14;
  if ($[18] !== open) {
    t13 = () => {
      if (!open) {
        return;
      }
      const handler_1 = e_1 => {
        if (e_1.key !== "Tab") {
          return;
        }
        const root = sheetRef.current;
        if (!root || isInPortaledLayer(e_1.target)) {
          return;
        }
        const focusables = root.querySelectorAll("a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex=\"-1\"])");
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (!first || !last) {
          return;
        }
        if (!e_1.shiftKey && document.activeElement === last) {
          e_1.preventDefault();
          first.focus();
        } else {
          if (e_1.shiftKey && document.activeElement === first) {
            e_1.preventDefault();
            last.focus();
          }
        }
      };
      document.addEventListener("keydown", handler_1);
      return () => document.removeEventListener("keydown", handler_1);
    };
    t14 = [open];
    $[18] = open;
    $[19] = t13;
    $[20] = t14;
  } else {
    t13 = $[19];
    t14 = $[20];
  }
  useEffect(t13, t14);
  if (!open) {
    return null;
  }
  let t15;
  if ($[21] !== sessionToken || $[22] !== setLocation) {
    t15 = value => {
      setLocation(value);
      clearSuggestions();
      if (value.trim().length < 2) {
        return;
      }
      const requestId = suggestRequestRef.current;
      debounceRef.current = setTimeout(async () => {
        setIsLoading(true);
        const results = await suggestPlaces(value, sessionToken);
        if (requestId !== suggestRequestRef.current) {
          return;
        }
        setIsLoading(false);
        setSuggestions(results);
        setShowSuggestions(results.length > 0);
      }, 300);
    };
    $[21] = sessionToken;
    $[22] = setLocation;
    $[23] = t15;
  } else {
    t15 = $[23];
  }
  const handleLocationChange = t15;
  let t16;
  if ($[24] !== sessionToken || $[25] !== setLocation || $[26] !== setPlace) {
    t16 = async suggestion => {
      clearSuggestions();
      setIsLoading(true);
      const place = await retrievePlace(suggestion.mapbox_id, sessionToken);
      setIsLoading(false);
      if (!place) {
        setRetrieveFailed(true);
        return;
      }
      setLocation(place.label || suggestion.name);
      setPlace(place);
    };
    $[24] = sessionToken;
    $[25] = setLocation;
    $[26] = setPlace;
    $[27] = t16;
  } else {
    t16 = $[27];
  }
  const handleSelect = t16;
  let canSearch;
  let handleKeyDown;
  let handleSearch;
  if ($[28] !== activeSuggestionIndex || $[29] !== commitSearch || $[30] !== handleSelect || $[31] !== hasPendingChanges || $[32] !== onClose || $[33] !== outOfArea || $[34] !== searchUrl || $[35] !== selectedPlace || $[36] !== showSuggestions || $[37] !== suggestions) {
    handleKeyDown = e_2 => {
      if (!showSuggestions || suggestions.length === 0) {
        if (e_2.key === "Enter") {
          handleSearch();
        }
        return;
      }
      if (e_2.key === "ArrowDown") {
        e_2.preventDefault();
        setActiveSuggestionIndex(i => Math.min(i + 1, suggestions.length - 1));
      } else {
        if (e_2.key === "ArrowUp") {
          e_2.preventDefault();
          setActiveSuggestionIndex(_temp3);
        } else {
          if (e_2.key === "Enter") {
            e_2.preventDefault();
            const active = suggestions[activeSuggestionIndex];
            if (active) {
              handleSelect(active);
            } else {
              handleSearch();
            }
          } else {
            if (e_2.key === "Escape") {
              clearSuggestions();
            }
          }
        }
      }
    };
    canSearch = !!searchUrl && !!selectedPlace && !outOfArea && hasPendingChanges;
    handleSearch = () => {
      if (!canSearch) {
        return;
      }
      commitSearch();
      onClose();
    };
    $[28] = activeSuggestionIndex;
    $[29] = commitSearch;
    $[30] = handleSelect;
    $[31] = hasPendingChanges;
    $[32] = onClose;
    $[33] = outOfArea;
    $[34] = searchUrl;
    $[35] = selectedPlace;
    $[36] = showSuggestions;
    $[37] = suggestions;
    $[38] = canSearch;
    $[39] = handleKeyDown;
    $[40] = handleSearch;
  } else {
    canSearch = $[38];
    handleKeyDown = $[39];
    handleSearch = $[40];
  }
  let t17;
  if ($[41] !== startsAt) {
    t17 = startsAt ? formatShort(startsAt) : null;
    $[41] = startsAt;
    $[42] = t17;
  } else {
    t17 = $[42];
  }
  const startDisplay = t17;
  let t18;
  if ($[43] !== endsAt) {
    t18 = endsAt ? formatShort(endsAt) : null;
    $[43] = endsAt;
    $[44] = t18;
  } else {
    t18 = $[44];
  }
  const endDisplay = t18;
  let t19;
  if ($[45] !== location || $[46] !== selectedPlace) {
    t19 = location.trim().length > 0 && !selectedPlace;
    $[45] = location;
    $[46] = selectedPlace;
    $[47] = t19;
  } else {
    t19 = $[47];
  }
  const needsPlace = t19;
  let t20;
  if ($[48] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = <h2 id="mobile-search-title" className="text-lg font-semibold leading-tight">Edit search</h2>;
    $[48] = t20;
  } else {
    t20 = $[48];
  }
  let t21;
  if ($[49] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = <X className="h-6 w-6" />;
    $[49] = t21;
  } else {
    t21 = $[49];
  }
  let t22;
  if ($[50] !== onClose) {
    t22 = <div className="flex shrink-0 items-start justify-between gap-3 border-b border-[color:var(--color-border)] px-4 pb-3 pt-5">{t20}<button type="button" onClick={onClose} className="-mr-1 -mt-1 shrink-0 rounded-full p-2.5 text-[color:var(--color-muted)] transition-colors hover:bg-[color:var(--color-surface-2)] hover:text-[color:var(--color-body)]" aria-label="Close">{t21}</button></div>;
    $[50] = onClose;
    $[51] = t22;
  } else {
    t22 = $[51];
  }
  let t23;
  if ($[52] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = <label htmlFor="mobile-location-input" className="mb-1.5 block text-[11px] font-bold uppercase tracking-wider text-[color:var(--color-muted)]">Location</label>;
    $[52] = t23;
  } else {
    t23 = $[52];
  }
  const t24 = showSuggestions ? "border-[color:var(--color-accent)] bg-[color:var(--color-surface)]" : "border-[color:var(--color-border)] bg-[color:var(--color-surface-2)]";
  let t25;
  if ($[53] !== t24) {
    t25 = cn("flex items-center gap-3 rounded-xl border transition-colors focus-within:border-[color:var(--color-accent)] focus-within:bg-[color:var(--color-surface)]", t24);
    $[53] = t24;
    $[54] = t25;
  } else {
    t25 = $[54];
  }
  let t26;
  if ($[55] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = <MapPin className="ml-3 h-4 w-4 shrink-0 text-[color:var(--color-secondary)]" />;
    $[55] = t26;
  } else {
    t26 = $[55];
  }
  let t27;
  if ($[56] !== handleLocationChange) {
    t27 = e_3 => handleLocationChange(e_3.target.value);
    $[56] = handleLocationChange;
    $[57] = t27;
  } else {
    t27 = $[57];
  }
  let t28;
  if ($[58] !== suggestions.length) {
    t28 = () => {
      if (suggestions.length > 0) {
        setShowSuggestions(true);
      }
    };
    $[58] = suggestions.length;
    $[59] = t28;
  } else {
    t28 = $[59];
  }
  const t29 = activeSuggestionIndex >= 0 ? `mobile-location-suggestion-${suggestions[activeSuggestionIndex]?.mapbox_id}` : undefined;
  const t30 = retrieveFailed ? "mobile-location-error" : needsPlace ? "mobile-location-hint" : undefined;
  let t31;
  if ($[60] !== handleKeyDown || $[61] !== location || $[62] !== showSuggestions || $[63] !== t27 || $[64] !== t28 || $[65] !== t29 || $[66] !== t30) {
    t31 = <input ref={locationInputRef} id="mobile-location-input" type="text" role="combobox" value={location} onChange={t27} onKeyDown={handleKeyDown} onFocus={t28} placeholder="Where do you need parking?" className="w-full border-none bg-transparent py-3 pr-3 text-[14px] font-medium text-[color:var(--color-body)] outline-none placeholder:font-normal placeholder:text-[color:var(--color-muted)]" autoComplete="off" aria-autocomplete="list" aria-expanded={showSuggestions} aria-controls="mobile-location-suggestions" aria-activedescendant={t29} aria-describedby={t30} />;
    $[60] = handleKeyDown;
    $[61] = location;
    $[62] = showSuggestions;
    $[63] = t27;
    $[64] = t28;
    $[65] = t29;
    $[66] = t30;
    $[67] = t31;
  } else {
    t31 = $[67];
  }
  let t32;
  if ($[68] !== isLoading || $[69] !== location.length || $[70] !== setLocation) {
    t32 = isLoading ? <span className="mr-3 inline-block h-3.5 w-3.5 shrink-0 animate-spin rounded-full border-2 border-[color:var(--color-border)] border-t-[color:var(--color-accent)]" /> : location.length > 0 ? <button type="button" aria-label="Clear location" onMouseDown={_temp4} onClick={() => {
      setLocation("");
      clearSuggestions();
      locationInputRef.current?.focus();
    }} className="mr-3 flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-full bg-[var(--ink-20)] transition-colors hover:bg-[var(--ink-40)]"><X className="h-2 w-2 text-black" strokeWidth={3} /></button> : null;
    $[68] = isLoading;
    $[69] = location.length;
    $[70] = setLocation;
    $[71] = t32;
  } else {
    t32 = $[71];
  }
  let t33;
  if ($[72] !== t25 || $[73] !== t31 || $[74] !== t32) {
    t33 = <div className={t25}>{t26}{t31}{t32}</div>;
    $[72] = t25;
    $[73] = t31;
    $[74] = t32;
    $[75] = t33;
  } else {
    t33 = $[75];
  }
  let t34;
  if ($[76] !== activeSuggestionIndex || $[77] !== handleSelect || $[78] !== showSuggestions || $[79] !== suggestions) {
    t34 = showSuggestions && suggestions.length > 0 && <ul id="mobile-location-suggestions" className="absolute left-0 top-full z-[200] mt-1 max-h-72 w-full touch-pan-y overflow-x-hidden overflow-y-auto rounded-xl border border-[color:var(--color-border)] bg-white shadow-lg" style={{
      WebkitOverflowScrolling: "touch"
    }}>{suggestions.map((suggestion_0, index) => <li key={suggestion_0.mapbox_id} role="presentation"><button id={`mobile-location-suggestion-${suggestion_0.mapbox_id}`} type="button" role="option" aria-selected={index === activeSuggestionIndex} className={cn("flex w-full items-start gap-2.5 px-3.5 py-2.5 text-left text-[13px] transition-colors", index === activeSuggestionIndex ? "bg-[#EAF6FF]" : "hover:bg-[#F5FBFF]")} onMouseDown={e_5 => {
          e_5.preventDefault();
          handleSelect(suggestion_0);
        }} onMouseEnter={() => setActiveSuggestionIndex(index)}><MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[color:var(--color-accent)]" /><span className="min-w-0"><span className="block truncate font-medium text-[color:var(--color-body)]">{suggestion_0.name}</span>{suggestion_0.place_formatted && <span className="block truncate text-[12px] text-[color:var(--color-secondary)]">{suggestion_0.place_formatted}</span>}</span></button></li>)}</ul>;
    $[76] = activeSuggestionIndex;
    $[77] = handleSelect;
    $[78] = showSuggestions;
    $[79] = suggestions;
    $[80] = t34;
  } else {
    t34 = $[80];
  }
  let t35;
  if ($[81] !== t33 || $[82] !== t34) {
    t35 = <div className="relative" ref={locationRef}>{t33}{t34}</div>;
    $[81] = t33;
    $[82] = t34;
    $[83] = t35;
  } else {
    t35 = $[83];
  }
  let t36;
  if ($[84] !== outOfArea || $[85] !== selectedPlace?.city || $[86] !== setLocation) {
    t36 = outOfArea && <OutOfAreaNotice city={selectedPlace?.city} onChangeDestination={() => {
      setLocation("");
      clearSuggestions();
      locationInputRef.current?.focus();
    }} />;
    $[84] = outOfArea;
    $[85] = selectedPlace?.city;
    $[86] = setLocation;
    $[87] = t36;
  } else {
    t36 = $[87];
  }
  let t37;
  if ($[88] !== isLoading || $[89] !== needsPlace || $[90] !== retrieveFailed || $[91] !== showSuggestions) {
    t37 = retrieveFailed ? <p id="mobile-location-error" role="alert" className="mt-1.5 text-[12px] text-(--red-text)">Couldn't load that address. Try it again or pick another.</p> : needsPlace && !showSuggestions && !isLoading && <p id="mobile-location-hint" className="mt-1.5 text-[12px] text-[color:var(--color-secondary)]">Choose a suggested address to search.</p>;
    $[88] = isLoading;
    $[89] = needsPlace;
    $[90] = retrieveFailed;
    $[91] = showSuggestions;
    $[92] = t37;
  } else {
    t37 = $[92];
  }
  let t38;
  if ($[93] !== t35 || $[94] !== t36 || $[95] !== t37) {
    t38 = <section>{t23}{t35}{t36}{t37}</section>;
    $[93] = t35;
    $[94] = t36;
    $[95] = t37;
    $[96] = t38;
  } else {
    t38 = $[96];
  }
  let t39;
  if ($[97] === Symbol.for("react.memo_cache_sentinel")) {
    t39 = <p className="mb-1.5 text-[11px] font-bold uppercase tracking-wider text-[color:var(--color-muted)]">{"Date & Time"}</p>;
    $[97] = t39;
  } else {
    t39 = $[97];
  }
  let t40;
  if ($[98] !== endDisplay || $[99] !== startDisplay) {
    t40 = (startDisplay ?? endDisplay) && <div className="mb-3 flex gap-2 text-[12px] text-[color:var(--color-muted)]">{startDisplay && <span>{startDisplay}</span>}{startDisplay && endDisplay && <span>→</span>}{endDisplay && <span>{endDisplay}</span>}</div>;
    $[98] = endDisplay;
    $[99] = startDisplay;
    $[100] = t40;
  } else {
    t40 = $[100];
  }
  let t41;
  if ($[101] !== destTimeZone || $[102] !== endsAt || $[103] !== now || $[104] !== setEndsAt || $[105] !== setStartsAt || $[106] !== startsAt) {
    t41 = <DateTimeFields startsAt={startsAt} endsAt={endsAt} onStartsAtChange={setStartsAt} onEndsAtChange={setEndsAt} now={now} timeZone={destTimeZone} />;
    $[101] = destTimeZone;
    $[102] = endsAt;
    $[103] = now;
    $[104] = setEndsAt;
    $[105] = setStartsAt;
    $[106] = startsAt;
    $[107] = t41;
  } else {
    t41 = $[107];
  }
  let t42;
  if ($[108] !== t40 || $[109] !== t41) {
    t42 = <section>{t39}{t40}{t41}</section>;
    $[108] = t40;
    $[109] = t41;
    $[110] = t42;
  } else {
    t42 = $[110];
  }
  let t43;
  if ($[111] === Symbol.for("react.memo_cache_sentinel")) {
    t43 = <p className="mb-1.5 text-[11px] font-bold uppercase tracking-wider text-[color:var(--color-muted)]">Vehicle size</p>;
    $[111] = t43;
  } else {
    t43 = $[111];
  }
  let t44;
  if ($[112] !== setVehicleSize || $[113] !== vehicleSize) {
    t44 = <section>{t43}<VehicleSizePicker value={vehicleSize} onChange={setVehicleSize} showLabel={false} /></section>;
    $[112] = setVehicleSize;
    $[113] = vehicleSize;
    $[114] = t44;
  } else {
    t44 = $[114];
  }
  let t45;
  if ($[115] !== t38 || $[116] !== t42 || $[117] !== t44) {
    t45 = <div className="flex-1 space-y-5 px-4 py-4">{t38}{t42}{t44}</div>;
    $[115] = t38;
    $[116] = t42;
    $[117] = t44;
    $[118] = t45;
  } else {
    t45 = $[118];
  }
  const t46 = !canSearch;
  let t47;
  if ($[119] === Symbol.for("react.memo_cache_sentinel")) {
    t47 = <Search className="mr-2 h-4 w-4 shrink-0" strokeWidth={2.5} />;
    $[119] = t47;
  } else {
    t47 = $[119];
  }
  let t48;
  if ($[120] !== handleSearch || $[121] !== t46) {
    t48 = <div className="shrink-0 border-t border-[color:var(--color-border)] px-4 py-4"><Button type="button" onClick={handleSearch} disabled={t46} variant="primary" size="md" className="w-full">{t47}Search</Button></div>;
    $[120] = handleSearch;
    $[121] = t46;
    $[122] = t48;
  } else {
    t48 = $[122];
  }
  let t49;
  if ($[123] !== t22 || $[124] !== t45 || $[125] !== t48) {
    t49 = <div ref={sheetRef} role="dialog" aria-modal="true" aria-labelledby="mobile-search-title" className="fixed inset-0 z-[100] flex flex-col bg-[color:var(--color-surface)] text-[color:var(--color-body)] md:hidden">{t22}{t45}{t48}</div>;
    $[123] = t22;
    $[124] = t45;
    $[125] = t48;
    $[126] = t49;
  } else {
    t49 = $[126];
  }
  return t49;
}
function _temp4(e_4) {
  return e_4.preventDefault();
}
function _temp3(i_0) {
  return Math.max(i_0 - 1, -1);
}
function _temp2() {
  document.body.style.overflow = "";
}
function _temp() {
  return safeUUID();
}